import { ROUTES } from "@/components/shared/constant";
import type { EmpPostType } from "../../../../shared/employee/feed";
import type { EmpReportReason } from "../../../../shared/employee/moderation";

/** The feed's filter in Home's URL (?feed=shout_out), so a link can open it filtered. */
export const FEED_PARAM = "feed";

/**
 * Only shout-outs naming this person, in Home's URL (?feed=shout_out&for=id):
 * where a profile's "See all shout-outs" goes. Applies with the Shout-outs
 * chip only; choosing another chip drops it.
 */
export const FOR_PARAM = "for";

/**
 * Home's URL showing the shout-outs naming someone. Only the instance is
 * carried over: the page it comes from has filters of its own (the
 * directory's ?location=) that mean something else on Home.
 */
export function shoutOutsForSearch(search: string, partyId: string): string {
  const from = new URLSearchParams(search);
  const params = new URLSearchParams();
  const instance = from.get("instanceid");
  if (instance) params.set("instanceid", instance);
  params.set(FEED_PARAM, "shout_out");
  params.set(FOR_PARAM, partyId);
  return `?${params.toString()}`;
}

/** Link state asking Home to scroll the feed into view (Recent shout-outs' "See all"). */
export const SCROLL_TO_FEED = { scrollToFeed: true } as const;

/**
 * The feed's filter chips. Posts, shout-outs and the rest, never a type no one
 * can see. Videos is by what a post carries rather than its kind (?feed=videos).
 */
export const FEED_FILTERS: {
  label: string;
  type: EmpPostType | undefined;
  media?: "video";
}[] = [
  { label: "All", type: undefined },
  { label: "Announcements", type: "announcement" },
  { label: "Spotlights", type: "spotlight" },
  { label: "Shout-outs", type: "shout_out" },
  { label: "Polls", type: "poll" },
  { label: "Posts", type: "post" },
  { label: "Videos", type: undefined, media: "video" },
  { label: "E-cards", type: "ecard" },
];

export const POST_TYPE_LABELS: Record<EmpPostType, string> = {
  post: "Post",
  shout_out: "Shout-out",
  announcement: "Announcement",
  spotlight: "Spotlight",
  poll: "Poll",
  ecard: "E-card",
};

export const REPORT_REASON_LABELS: Record<EmpReportReason, string> = {
  privacy: "It shares private or resident information",
  harassment: "It is offensive or harassing",
  spam: "It is spam or not work related",
  misinformation: "It is false or misleading",
  inappropriate: "It is inappropriate for work",
  other: "Something else",
};

/** The page for one post (P3-6), keeping the instance query string. */
export function postPath(postId: string, search = ""): string {
  return `${ROUTES.EMPLOYEE_MY_HOME}/post/${postId}${search}`;
}

/** The file a poll's exported results are saved as. */
export function pollResultsFilename(postId: string): string {
  return `poll-results-${postId.slice(0, 8)}.csv`;
}
